import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Check, Code, Eye, Image, Loader2, Newspaper, Plus, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useCurrentUser } from '@/contexts/UserContext'
import { AuthService } from '@/services/core/auth'
import { apiErrorDescription, ApiError } from '@/services/core/apiError'
import {
  INTERNAL_USER_GROUP,
  formatReleaseNoteAsset,
  isReleaseNotesAdmin,
  parseReleaseNoteAsset,
  RELEASE_NOTE_APPS,
  RELEASE_NOTE_TAGS,
  ReleaseNotesApi,
  type ReleaseNote,
  type ReleaseNoteApp,
  type ReleaseNoteInput,
  type ReleaseNoteState,
  type ReleaseNoteStatus,
  type ReleaseNoteTag,
  uploadReleaseNoteAsset,
} from '@/services/releaseNotes/releaseNotesApi'
import { AppShell } from '../../components/AppShell'
import { BadgeToggleGroup } from '../../components/BadgeToggleGroup'
import { DateTimePicker } from '../../components/DateTimePicker'
import { FieldLabel } from '../../components/FieldLabel'
import { FormSkeleton } from '../../components/FormSkeleton'
import { HtmlEditor } from '../../components/HtmlEditor'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { ListTabs } from '../../components/ListTabs'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { ReleaseNoteAssets } from '../../components/ReleaseNoteAssets'
import { ReleaseNoteCard } from '../../components/ReleaseNoteCard'
import { StatsBar, StatsBarActivity, StatsBarId } from '../../components/StatsBar'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { formatDate } from '../../utils/format'

type PublishMode = 'IMMEDIATE' | 'CUSTOM'
type Visibility = 'PUBLIC' | 'INTERNAL'

// A note published "immediately" has the time it went out filled in by the
// server, so from then on it reads as a custom time — as in UII.
function toFormValues(note: ReleaseNote | null) {
  return {
    title: note?.title ?? '',
    subTitle: note?.subTitle ?? '',
    content: note?.content ?? '',
    publishMode: (note?.publishTime ? 'CUSTOM' : 'IMMEDIATE') as PublishMode,
    publishTime: note?.publishTime ?? '',
    visibility: (note?.userGroups.length ? 'INTERNAL' : 'PUBLIC') as Visibility,
    status: note?.status ?? ('DRAFT' as ReleaseNoteStatus),
    state: note?.state ?? ('ACTIVE' as ReleaseNoteState),
    apps: note?.apps ?? (['MARKETPLACE', 'ADSERVER'] as ReleaseNoteApp[]),
    tags: note?.tags ?? ([] as ReleaseNoteTag[]),
    assetStrings: note?.assetStrings ?? [],
  }
}

// ── Inner form component ────────────────────────────────────────────────────

interface ReleaseNoteEditFormProps {
  note: ReleaseNote | null
  id: string
  isNew: boolean
}

function ReleaseNoteEditForm({ note, id, isNew }: ReleaseNoteEditFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const form = useForm({
    defaultValues: toFormValues(note),
    onSubmit: ({ value }) => {
      mutation.mutate({
        title: value.title.trim(),
        subTitle: value.subTitle,
        content: value.content,
        status: value.status,
        state: value.state,
        publishTime: value.publishMode === 'CUSTOM' ? value.publishTime : '',
        apps: value.apps,
        tags: value.tags,
        userGroups: value.visibility === 'INTERNAL' ? [INTERNAL_USER_GROUP] : [],
        assetStrings: value.assetStrings,
      })
    },
  })
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)

  const mutation = useMutation({
    mutationFn: (input: ReleaseNoteInput) => ReleaseNotesApi.save(id, input, note),
    onSuccess: (data) => {
      queryClient.setQueryData(['release-note', id], data)
      queryClient.invalidateQueries({ queryKey: ['release-notes'] })
      // What was saved is what Cancel goes back to from now on. Reset to the
      // server's version: the form's defaults are rebuilt from it on the next
      // render, and anything the server filled in would otherwise replace the
      // values and leave the form dirty, asking for a second save.
      const saved = toFormValues(data)
      form.reset(saved)
      markClean(saved)
      if (isNew) navigate(`/super-admin/release-notes/${id}`, { replace: true })
      toast.success(t('common.saved'), {
        description: isNew ? t('releaseNotes.createdSuccess') : t('releaseNotes.savedSuccess'),
        duration: 3000,
      })
    },
    onError: (error) => {
      const summary =
        error instanceof ApiError
          ? error.message
          : isNew
            ? t('releaseNotes.createFailed')
            : t('releaseNotes.saveFailed')
      toast.error(summary, { description: apiErrorDescription(error), duration: 5000 })
    },
  })

  const crumbs = [
    { label: t('releaseNotes.title'), href: '/super-admin/release-notes' },
    {
      label: isNew
        ? t('releaseNotes.newReleaseNote')
        : (note?.title ?? t('releaseNotes.editTitle')),
    },
  ]

  const appOptions = RELEASE_NOTE_APPS.map((app) => ({
    value: app,
    label: t(`releaseNotes.appNames.${app}`),
  }))
  const tagOptions = RELEASE_NOTE_TAGS.map((tag) => ({
    value: tag,
    label: t(`releaseNotes.tagNames.${tag}`),
  }))

  return (
    <AppShell
      crumbs={crumbs}
      showBack
      objectType={isNew ? undefined : t('objectTypes.releaseNote')}
    >
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.releaseNote')} />
            <h1>
              <form.Field
                name="title"
                validators={{
                  onChange: ({ value }) =>
                    value.trim() ? undefined : t('releaseNotes.titleRequired'),
                  onSubmit: ({ value }) =>
                    value.trim() ? undefined : t('releaseNotes.titleRequired'),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('releaseNotes.titlePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
          </div>
        </div>

        {note && !isNew && (
          <StatsBar
            items={[
              { label: t('common.id'), content: <StatsBarId value={note.id} /> },
              {
                label: t('stats.activity'),
                content: (
                  <StatsBarActivity
                    created={formatDate(note.createTime)}
                    updated={formatDate(note.updateTime)}
                  />
                ),
              },
              {
                label: t('releaseNotes.author'),
                content: (
                  <span className="text-xs text-(--ad-text-muted)">
                    {t('releaseNotes.author')}: {note.author || '—'}
                  </span>
                ),
              },
              {
                label: t('releaseNotes.urlSlug'),
                content: (
                  <span className="text-xs text-(--ad-text-muted)">
                    {t('releaseNotes.urlSlug')}: <span className="font-mono">{note.urlSlug}</span>
                  </span>
                ),
              },
            ]}
          />
        )}

        <div className="flex flex-col gap-3.5 lg:flex-row lg:items-start">
          <div className="flex min-w-0 flex-1 flex-col gap-3.5">
            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Newspaper} />
                  {t('releaseNotes.details')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="field-grid">
                  <div className="p-field full">
                    <FieldLabel>{t('releaseNotes.subTitle')}</FieldLabel>
                    <form.Field name="subTitle">
                      {(field) => (
                        <Input
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          placeholder={t('releaseNotes.subTitlePlaceholder')}
                        />
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field">
                    <FieldLabel tooltip={t('releaseNotes.statusHelp')}>
                      {t('common.status')}
                    </FieldLabel>
                    <form.Field name="status">
                      {(field) => (
                        <ListTabs
                          className="self-start"
                          tabs={[
                            { key: 'DRAFT', label: t('releaseNotes.statuses.DRAFT') },
                            { key: 'PUBLISHED', label: t('releaseNotes.statuses.PUBLISHED') },
                          ]}
                          value={field.state.value}
                          onValueChange={(v) => field.handleChange(v)}
                        />
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field">
                    <FieldLabel tooltip={t('releaseNotes.visibilityHelp')}>
                      {t('releaseNotes.visibility')}
                    </FieldLabel>
                    <form.Field name="visibility">
                      {(field) => (
                        <ListTabs
                          className="self-start"
                          tabs={[
                            { key: 'PUBLIC', label: t('releaseNotes.visibilities.PUBLIC') },
                            { key: 'INTERNAL', label: t('releaseNotes.visibilities.INTERNAL') },
                          ]}
                          value={field.state.value}
                          onValueChange={(v) => field.handleChange(v)}
                        />
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field">
                    <FieldLabel tooltip={t('releaseNotes.publishTimeHelp')}>
                      {t('releaseNotes.publishTime')}
                    </FieldLabel>
                    <form.Field name="publishMode">
                      {(field) => (
                        <ListTabs
                          className="self-start"
                          tabs={[
                            { key: 'IMMEDIATE', label: t('releaseNotes.publishModes.IMMEDIATE') },
                            { key: 'CUSTOM', label: t('releaseNotes.publishModes.CUSTOM') },
                          ]}
                          value={field.state.value}
                          onValueChange={(v) => field.handleChange(v)}
                        />
                      )}
                    </form.Field>
                    <form.Subscribe selector={(s) => s.values.publishMode}>
                      {(publishMode) =>
                        publishMode === 'CUSTOM' && (
                          <form.Field
                            name="publishTime"
                            validators={{
                              onSubmit: ({ value, fieldApi }) =>
                                fieldApi.form.getFieldValue('publishMode') === 'CUSTOM' && !value
                                  ? t('releaseNotes.publishTimeRequired')
                                  : undefined,
                            }}
                          >
                            {(field) => (
                              <>
                                <DateTimePicker
                                  value={field.state.value}
                                  onChange={(v) => field.handleChange(v)}
                                />
                                {field.state.meta.errors[0] && (
                                  <p className="text-xs text-destructive">
                                    {field.state.meta.errors[0] as string}
                                  </p>
                                )}
                              </>
                            )}
                          </form.Field>
                        )
                      }
                    </form.Subscribe>
                  </div>
                  <div className="p-field">
                    <FieldLabel tooltip={t('releaseNotes.stateHelp')}>
                      {t('releaseNotes.state')}
                    </FieldLabel>
                    <form.Field name="state">
                      {(field) => (
                        <ListTabs
                          className="self-start"
                          tabs={[
                            { key: 'ACTIVE', label: t('releaseNotes.states.ACTIVE') },
                            { key: 'ARCHIVED', label: t('releaseNotes.states.ARCHIVED') },
                          ]}
                          value={field.state.value}
                          onValueChange={(v) => field.handleChange(v)}
                        />
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field">
                    <form.Field name="apps">
                      {(field) => (
                        <BadgeToggleGroup
                          label={t('releaseNotes.apps')}
                          options={appOptions}
                          value={field.state.value}
                          onChange={(v) => field.handleChange(v as ReleaseNoteApp[])}
                        />
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field">
                    <form.Field name="tags">
                      {(field) => (
                        <BadgeToggleGroup
                          label={t('releaseNotes.tags')}
                          options={tagOptions}
                          value={field.state.value}
                          onChange={(v) => field.handleChange(v as ReleaseNoteTag[])}
                        />
                      )}
                    </form.Field>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card tone="creative">
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Code} />
                  {t('releaseNotes.content')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-2">
                  <p className="text-xs text-muted-foreground">{t('releaseNotes.contentHelp')}</p>
                  <form.Subscribe selector={(s) => s.values.assetStrings}>
                    {(assetStrings) => (
                      <form.Field name="content">
                        {(field) => (
                          <HtmlEditor
                            value={field.state.value}
                            onChange={(v) => field.handleChange(v)}
                            placeholder={t('releaseNotes.contentPlaceholder')}
                            minHeight={320}
                            markdown
                            images={assetStrings.map((a) => {
                              const asset = parseReleaseNoteAsset(a)
                              return { src: asset.cdnId, label: asset.fileName }
                            })}
                            onUploadImage={async (file) => {
                              const asset = await uploadReleaseNoteAsset(file)
                              // Listed under the note's assets too, as if uploaded there.
                              form.setFieldValue('assetStrings', (current) => [
                                ...current,
                                formatReleaseNoteAsset(asset),
                              ])
                              return {
                                src: asset.cdnId,
                                alt: asset.fileName,
                                width: asset.width || undefined,
                                height: asset.height || undefined,
                              }
                            }}
                          />
                        )}
                      </form.Field>
                    )}
                  </form.Subscribe>
                </div>
              </CardContent>
            </Card>

            <Card tone="creative">
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Image} />
                  {t('releaseNotes.assets')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form.Field name="assetStrings">
                  {(field) => (
                    <ReleaseNoteAssets
                      value={field.state.value}
                      onChange={(v) => field.handleChange(v)}
                    />
                  )}
                </form.Field>
              </CardContent>
            </Card>
          </div>

          <Card tone="creative" className="min-w-0 flex-1">
            <CardHeader>
              <CardTitle>
                <CardIcon icon={Eye} />
                {t('releaseNotes.preview')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form.Subscribe selector={(s) => s.values}>
                {(values) => (
                  <ReleaseNoteCard
                    title={values.title}
                    subTitle={values.subTitle}
                    publishTime={
                      values.publishMode === 'CUSTOM' ? values.publishTime : note?.publishTime
                    }
                    internal={values.visibility === 'INTERNAL'}
                    apps={values.apps}
                    tags={values.tags}
                    content={values.content}
                  />
                )}
              </form.Subscribe>
            </CardContent>
          </Card>
        </div>

        <div
          className={`detail-savebar${isNew || isDirty || mutation.isPending ? ' is-visible' : ''}`}
        >
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {isNew ? t('common.creating') : t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                if (isNew) return navigate('/super-admin/release-notes')
                form.reset()
                markClean()
              }}
              disabled={mutation.isPending}
            >
              <X className="size-4" />
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {isNew ? <Plus className="size-4" /> : <Check className="size-4" />}
              {isNew ? t('releaseNotes.newReleaseNote') : t('common.save')}
            </Button>
          </div>
        </div>
      </form>
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

// ── Outer page ──────────────────────────────────────────────────────────────

export function ReleaseNoteEditPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isNew = searchParams.get('new') === 'true'
  const hasToken = AuthService.isAuthenticated()
  const currentUser = useCurrentUser()
  const isAdmin = isReleaseNotesAdmin(currentUser?.username)

  const {
    data: note,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['release-note', id],
    queryFn: () => ReleaseNotesApi.getById(id!),
    enabled: !!id && !isNew && hasToken && isAdmin,
    retry: false,
  })

  // The backend reads and saves release notes for its own admin accounts only.
  if (currentUser && !isAdmin) return <Navigate to="/select-module" replace />

  const crumbsLoading = [
    { label: t('releaseNotes.title'), href: '/super-admin/release-notes' },
    { label: isNew ? t('releaseNotes.newReleaseNote') : t('releaseNotes.editTitle') },
  ]

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <div className="ad-page">
          <p className="text-sm text-destructive">
            {apiErrorDescription(error) || t('releaseNotes.loadFailed')}
          </p>
        </div>
      </AppShell>
    )
  }

  if ((isLoading || !currentUser) && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <FormSkeleton rows={3} columns={2} />
      </AppShell>
    )
  }

  return <ReleaseNoteEditForm key={id} note={note ?? null} id={id!} isNew={isNew} />
}
